/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useCallback} from "react";
import {useSelector} from "react-redux";
import {MutableList} from "./MutableList";
import {useEditContextNodeId} from "../../../Hooks/PermissionHooks";

/**
 * Custom list component for the ProjectStakeholderRelationship association on Stakeholder.
 *
 * Wiring overview:
 *   ChildrenFieldEditor (the association editor for ArrayOfChildren fields) resolves this component
 *   by name from ListComponents.js (via `listComponentOverride: 'StreamBankProjectStakeholderList'`
 *   in the Stakeholder model), then renders it with the following key props:
 *
 *   - Data: the current array of ProjectStakeholderRelationship records
 *   - UpdateItem / UpdateItems: callbacks to update one or all records
 *   - AddItem(item?): callback that merges `item` into model defaults and appends a new row
 *   - RemoveItem: callback to remove a row
 *   - Type: GenericModelViewerListNode — renders each row via the model's groupEditor
 *     (CompactModelViewerRenderer), which in turn renders each field
 *     via CompactModelViewerRecordRenderer
 *   - AdditionalTypeProps: passed through to each row's ItemComponent, containing
 *     { FieldDef, Editable, OnValidationUpdate, CustomData }
 *
 * This component:
 *   1. Restricts edit access to rows whose contextNodeId is in the user's editable set
 *   2. Suppresses funderId validation (not applicable here)
 *   3. Pre-fills projectId from the current route context when adding a new row
 */
export function StreamBankProjectStakeholderList(props) {

    const {
        AdditionalTypeProps,
        AddItem,
    } = props;

    const editContextNodeId = useEditContextNodeId();

    // Pre-fill projectId from Redux route context when adding a new relationship row.
    // context.projectId is set by ContextBuilderHooks whenever the URL includes a project
    // segment (e.g. /programs/srsp/projects/snake-rm-123/contacts/456). On the global
    // contacts page (no project in URL) it will be null, so we leave it unset.
    const contextProjectId = useSelector(state => state.contextState.context?.projectId ?? null);

    const addItemWithProject = useCallback((item = null) => {
        AddItem(contextProjectId ? { projectId: contextProjectId, ...item } : item);
    }, [AddItem, contextProjectId]);

    // New ProjectStakeholderRelationships don't have a contextNodeId yet,
    // but we should allow the user to edit them (that's why the check allows null).
    const canEditThisContextNode = (item) => {
        const {contextNodeId} = item;
        return !contextNodeId || editContextNodeId(contextNodeId);
    };

    const customValidator = (validation, editorGuid) => {
        // funderId is not a real field on ProjectStakeholderRelationship;
        // suppress any spurious validation errors it generates.
        const funderIdValidation = validation['funderId'];
        if (funderIdValidation) {
            validation['funderId'] = {
                className: '',
                message: null,
                valid: true,
            };
        }

        AdditionalTypeProps.OnValidationUpdate(validation, editorGuid);
    };

    return <MutableList
        // Spread props first so our explicit overrides below win over whatever
        // ChildrenFieldEditor passed in (AddItem, AdditionalTypeProps, etc.).
        {...props}
        isItemEditable={canEditThisContextNode}
        AdditionalTypeProps={{
            ...AdditionalTypeProps,
            OnValidationUpdate: customValidator,
        }}
        AddItem={addItemWithProject}
    />;
}
